import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Search, Star } from 'lucide-react';

import PageContainer from '../components/PageContainer';
import { NotificationDetailModal } from '../components/notifications/NotificationDetailModal';
import { NotificationRow } from '../components/notifications/NotificationRow';
import { SeverityTabs, defaultSeverity } from '../components/notifications/SeverityTabs';
import {
    NOTIFICATION_SOURCES,
    bySource,
    countKeys,
    sourceIcons,
    sourceLabels,
    sourcePages,
} from '../components/notifications/notificationMeta';
import { useNotifications } from '../components/notifications/useNotifications';
import { Button, EmptyState, Input, LoadingState, Select } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import type { DashboardNotificationItem, DashboardNotificationSeverity, DashboardNotificationSource } from '../types';
import { cn } from '../utils/cn';
import { matchesQuery } from '../utils/search';

type Group = DashboardNotificationSource | 'STARRED';

export default function Notifications() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const { data, loading, load, markRead, toggleStar, setPriority } = useNotifications({ full: true });
    const [campus, setCampus] = useState('');
    const [query, setQuery] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [detail, setDetail] = useState<DashboardNotificationItem | null>(null);

    const grouped = useMemo(() => bySource(data.items), [data.items]);
    const starred = data.starred ?? [];
    const sourceCount = (source: DashboardNotificationSource) =>
        Math.max(data.counts[countKeys[source]] ?? 0, grouped[source]?.length ?? 0);
    const unreadCount = (source: DashboardNotificationSource) =>
        data.unreadCounts?.[countKeys[source]] ?? (grouped[source] ?? []).filter(item => !item.read).length;

    // The group from the link (bell → "Xem tất cả"), else the first one with something unread once loaded.
    // It stays put afterwards, so marking a group as seen doesn't jump to another one.
    const requested = searchParams.get('group') as Group | null;
    const [selected, setSelected] = useState<Group | null>(
        requested === 'STARRED' || NOTIFICATION_SOURCES.includes(requested as DashboardNotificationSource) ? requested : null,
    );
    useEffect(() => {
        if (loading || selected) return;
        setSelected(NOTIFICATION_SOURCES.find(source => unreadCount(source) > 0)
            ?? NOTIFICATION_SOURCES.find(source => sourceCount(source) > 0)
            ?? 'ATTENDANCE');
        // Only the first load picks the group.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);
    const group: Group = selected ?? 'ATTENDANCE';
    const selectGroup = (next: Group) => setSelected(next);

    const groupItems = group === 'STARRED' ? starred : grouped[group] ?? [];
    const campuses = useMemo(
        () => [...new Set(groupItems.map(item => item.campusName).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b, 'vi')),
        [groupItems],
    );
    const visible = groupItems.filter(item => (!campus || item.campusName === campus)
        && (!unreadOnly || !item.read)
        && matchesQuery(`${item.title} ${item.description}`, query));
    // Each group opens on its most pressing tab; filters keep the tab.
    const [severity, setSeverity] = useState<DashboardNotificationSeverity>('high');
    useEffect(() => {
        if (!loading) setSeverity(defaultSeverity(groupItems));
        // Only when the group changes or the list first loads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group, loading]);
    const shown = group === 'STARRED' ? visible : visible.filter(item => item.severity === severity);
    const visibleUnread = shown.filter(item => !item.read);
    const listedAll = group === 'STARRED' || sourceCount(group) <= groupItems.length;
    const detailItem = detail
        ? [...data.items, ...starred].find(item => item.id === detail.id) ?? detail
        : null;

    const openDetail = (item: DashboardNotificationItem) => {
        setDetail(item);
        markRead([item]);
    };

    const navItems: Array<{ key: Group; label: string; total: number; unread: number; Icon: typeof Star }> = [
        ...NOTIFICATION_SOURCES.map(source => ({
            key: source as Group,
            label: sourceLabels[source],
            total: sourceCount(source),
            unread: unreadCount(source),
            Icon: sourceIcons[source],
        })),
        { key: 'STARRED', label: 'Đã gắn sao', total: starred.length, unread: starred.filter(item => !item.read).length, Icon: Star },
    ];

    return (
        <PageContainer
            title="Thông báo"
            subtitle="Mọi cảnh báo cần chú ý, chia theo nhóm và mức độ. Đã xem và gắn sao là của riêng bạn."
            actions={(
                <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => load()}>
                    Làm mới
                </Button>
            )}
        >
            <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
                <nav aria-label="Nhóm thông báo" className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:overflow-visible lg:px-0">
                    <div className="flex gap-2 lg:flex-col lg:gap-1">
                        {navItems.map(({ key, label, total, unread, Icon }) => {
                            const selected = key === group;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    aria-current={selected ? 'page' : undefined}
                                    onClick={() => { selectGroup(key); setCampus(''); }}
                                    className={cn(
                                        'flex shrink-0 items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200 lg:w-full',
                                        selected ? 'border-academy-300 bg-academy-50 font-semibold text-academy-800' : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50 lg:border-transparent lg:bg-transparent',
                                    )}
                                >
                                    <Icon className={cn('h-4 w-4 shrink-0', key === 'STARRED' ? 'text-warning-500' : 'text-ink-500')} />
                                    <span className="whitespace-nowrap lg:flex-1">{label}</span>
                                    {unread > 0 ? (
                                        <span className="rounded-full bg-danger-600 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white" aria-label={`${unread} chưa xem`}>
                                            {unread}
                                        </span>
                                    ) : null}
                                    <span className="text-xs tabular-nums text-ink-500">{total}</span>
                                </button>
                            );
                        })}
                    </div>
                </nav>

                <div className="min-w-0 space-y-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <Input
                            aria-label="Tìm thông báo"
                            placeholder="Tìm theo học sinh, lớp…"
                            leftIcon={<Search className="h-4 w-4" />}
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            className="sm:w-64"
                        />
                        {campuses.length > 1 ? (
                            <Select aria-label="Lọc theo cơ sở" className="min-h-10 sm:w-44" value={campus} onChange={event => setCampus(event.target.value)}>
                                <option value="">Tất cả cơ sở</option>
                                {campuses.map(name => <option key={name} value={name}>{name}</option>)}
                            </Select>
                        ) : null}
                        <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                            <input type="checkbox" className="h-4 w-4 rounded border-ink-300" checked={unreadOnly} onChange={event => setUnreadOnly(event.target.checked)} />
                            Chỉ hiện chưa xem
                        </label>
                        <div className="sm:ml-auto">
                            <Button variant="secondary" size="sm" disabled={!visibleUnread.length} onClick={() => markRead(visibleUnread)}>
                                Đánh dấu đã xem tất cả{visibleUnread.length ? ` (${visibleUnread.length})` : ''}
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <LoadingState title="Đang tải thông báo" />
                    ) : visible.length === 0 ? (
                        <EmptyState
                            title={groupItems.length ? 'Không có thông báo khớp bộ lọc' : group === 'STARRED' ? 'Chưa gắn sao thông báo nào' : 'Không có thông báo nào'}
                        />
                    ) : (
                        <section className="rounded-lg border border-ink-200 bg-white">
                            {group !== 'STARRED' ? <SeverityTabs items={visible} active={severity} onChange={setSeverity} className="px-2" /> : null}
                            <div className="space-y-0.5 p-2">
                                {shown.length ? shown.map(item => (
                                    <NotificationRow
                                        key={item.id}
                                        item={item}
                                        onOpen={openDetail}
                                        onMarkRead={entry => markRead([entry])}
                                        onToggleStar={toggleStar}
                                        showSource={group === 'STARRED'}
                                        withDate={group === 'STARRED'}
                                    />
                                )) : <p className="px-2 py-6 text-center text-sm text-ink-500">Không có thông báo ở mức này.</p>}
                            </div>
                        </section>
                    )}

                    {!loading && group !== 'STARRED' && !listedAll ? (
                        <p className="text-xs text-ink-500">
                            Đang hiện {groupItems.length}/{sourceCount(group)} cảnh báo mới nhất.{' '}
                            <button type="button" onClick={() => navigate(sourcePages[group])} className="font-medium text-academy-700 hover:underline">
                                Xem đủ ở trang {sourceLabels[group]}
                            </button>
                        </p>
                    ) : null}
                </div>
            </div>

            <NotificationDetailModal
                item={detailItem}
                canChangePriority={user?.role === 'ADMIN' || user?.role === 'MANAGER'}
                onClose={() => setDetail(null)}
                onGoTo={item => navigate(item.href)}
                onSetPriority={setPriority}
                onToggleStar={toggleStar}
                onMarkUnread={item => markRead([item], false)}
            />
        </PageContainer>
    );
}
